import {
  ANTHROPIC_MODEL_IDS,
  type AnthropicModelId,
  type AppSettings,
  BRIEFING_PROVIDER_LABELS,
  BRIEFING_PROVIDERS,
  type BriefingModel,
  type BriefingProviderId,
  isAnthropicModelId,
  isMistralModelId,
  MISTRAL_MODEL_IDS,
  type MistralModelId,
  MODEL_LABELS,
  OPENROUTER_BRIEFING_SUGGESTIONS,
  type OpenRouterCatalogueEntry,
  openRouterModelIdSchema,
} from '@acasa/shared'
import { useEffect, useId, useRef, useState } from 'react'
import { Card } from '@/components/card'
import { useSession } from '@/features/auth/use-auth'
import { CONTROL, Field, Segmented, TextArea } from '@/features/handovers/fields'
import { ApiRequestError } from '@/lib/api'
import { useAppSettings, useUpdateAppSettings } from './use-app-settings'
import { useBriefingModels } from './use-briefing-models'

/**
 * Objekt-Briefings — which model writes them and what it is told to look for.
 *
 * Edits the `briefings` group and NOTHING else: the draft, the dirty check and
 * "Standard wiederherstellen" are all typed and keyed on that group alone, so a
 * save in another card cannot reset what is being typed here, and restoring the
 * defaults here cannot reach into a group this card does not show.
 *
 * The instruction is the CONTENT of the prompt. The structure of a briefing,
 * the rules that keep the model to the documents and the layout of the PDF are
 * fixed by the tool — the card says so, because an admin who writes "Schrift
 * Verdana, 9 pt" here would otherwise wonder why the PDF ignores it.
 *
 * Admin only, like the rest of the settings; the API answers 403 otherwise.
 */

type Briefings = AppSettings['briefings']

/** Mirrors the server's limits, so the count is honest about what will be refused. */
const INSTRUCTIONS_MIN = 50
const INSTRUCTIONS_MAX = 8000
const QUESTIONS_MAX = 2000

/** What a provider switch lands on when nothing valid was typed for it yet. */
// The Anthropic default comes from the server's `defaults` (DEFAULT_APP_SETTINGS);
// this is only the floor for a defaults object that names another provider.
const FALLBACK_ANTHROPIC_MODEL: AnthropicModelId = ANTHROPIC_MODEL_IDS[0]
const DEFAULT_MISTRAL_MODEL: MistralModelId = 'mistral-medium-2604'
const DEFAULT_OPENROUTER_MODEL = OPENROUTER_BRIEFING_SUGGESTIONS[0]?.id ?? ''

// The segmented control has to fit a 390px phone with three segments, so it
// carries a shorter caption for Mistral; the full label is used everywhere else.
const SEGMENT_LABELS: Partial<Record<BriefingProviderId, string>> = { mistral: 'Mistral (EU)' }

const PROVIDER_OPTIONS = BRIEFING_PROVIDERS.map((provider) => ({
  value: provider,
  label: SEGMENT_LABELS[provider] ?? BRIEFING_PROVIDER_LABELS[provider],
}))

function normaliseModel(model: BriefingModel): BriefingModel {
  // Lower-cased like the shared schema does on save, or a mixed-case draft
  // reads as dirty against what the server stored.
  return model.provider === 'openrouter'
    ? { provider: 'openrouter', id: model.id.trim().toLowerCase() }
    : model
}

/** The zod message for an OpenRouter id that will be refused, else null. */
function openRouterIdProblem(id: string): string | null {
  const result = openRouterModelIdSchema.safeParse(id)
  return result.success ? null : (result.error.issues[0]?.message ?? 'Ungültige Modell-ID.')
}

/** The model a switch to `provider` lands on: the last valid id typed for it, else its default. */
function modelFor(
  provider: BriefingProviderId,
  remembered: string | undefined,
  defaults: Briefings | null,
): BriefingModel {
  if (provider === 'anthropic') {
    const fallback =
      defaults?.model.provider === 'anthropic' ? defaults.model.id : FALLBACK_ANTHROPIC_MODEL
    return {
      provider,
      id: remembered && isAnthropicModelId(remembered) ? remembered : fallback,
    }
  }
  if (provider === 'mistral') {
    const fallback =
      defaults?.model.provider === 'mistral' ? defaults.model.id : DEFAULT_MISTRAL_MODEL
    return {
      provider,
      id: remembered && isMistralModelId(remembered) ? remembered : fallback,
    }
  }
  return {
    provider,
    id:
      remembered && openRouterIdProblem(remembered) === null
        ? remembered
        : DEFAULT_OPENROUTER_MODEL,
  }
}

function capabilityHint(entry: OpenRouterCatalogueEntry): string {
  const pdf = entry.nativePdf
    ? 'Nimmt PDFs direkt an'
    : entry.images
      ? 'PDF wird von OpenRouter vorab in Text (und bis zu 8 Seitenbilder) umgewandelt'
      : 'PDF wird von OpenRouter vorab in Text umgewandelt — Seitenbilder kann das Modell nicht lesen'
  const format = entry.structuredOutputs
    ? 'Antwortformat wird erzwungen'
    : 'Antwortformat nur per Anweisung'
  return `${pdf} · ${format}`
}

function normalise(value: Briefings): Briefings {
  return {
    model: normaliseModel(value.model),
    instructions: value.instructions.trim(),
    defaultSpecialQuestions: value.defaultSpecialQuestions.trim(),
  }
}

function same(a: Briefings, b: Briefings): boolean {
  const left = normalise(a)
  const right = normalise(b)
  return (
    left.model.provider === right.model.provider &&
    left.model.id === right.model.id &&
    left.instructions === right.instructions &&
    left.defaultSpecialQuestions === right.defaultSpecialQuestions
  )
}

export function BriefingSettingsCard() {
  const { user } = useSession()
  const isAdmin = user?.role === 'admin'

  const query = useAppSettings(isAdmin)
  const save = useUpdateAppSettings()

  const server = query.data?.settings.briefings ?? null
  const defaults = query.data?.defaults.briefings ?? null
  const [draft, setDraft] = useState<Briefings | null>(null)
  const [saved, setSaved] = useState(false)
  const counterId = useId()
  const datalistId = useId()
  const modelHintId = useId()
  // The id's format error waits for a blur or a "/" — not the first keystroke.
  const [idTouched, setIdTouched] = useState(false)

  // The last id per provider, so flipping Anthropic → OpenRouter → Anthropic
  // gives back what was there instead of the default.
  const rememberedIds = useRef<Partial<Record<BriefingProviderId, string>>>({})

  const catalogue = useBriefingModels(isAdmin && draft?.model.provider === 'openrouter')

  // Structural sharing keeps `briefings` the same object unless ITS values
  // change, so a save in another card does not reset a half-edited text here.
  useEffect(() => {
    setDraft(server ? { ...server } : null)
    if (server) rememberedIds.current[server.model.provider] = server.model.id
  }, [server])

  if (!isAdmin) return null

  const busy = save.isPending
  const instructionsLength = draft?.instructions.trim().length ?? 0
  const instructionsProblem =
    draft === null
      ? null
      : instructionsLength < INSTRUCTIONS_MIN
        ? `Mindestens ${INSTRUCTIONS_MIN} Zeichen.`
        : instructionsLength > INSTRUCTIONS_MAX
          ? `Höchstens ${INSTRUCTIONS_MAX.toLocaleString('de-DE')} Zeichen.`
          : null
  const modelProblem =
    draft?.model.provider === 'openrouter' ? openRouterIdProblem(draft.model.id) : null
  const showModelProblem =
    modelProblem !== null && (idTouched || (draft?.model.id.includes('/') ?? false))
  const trimmedModelId = draft?.model.id.trim().toLowerCase() ?? ''
  const catalogueEntry =
    draft?.model.provider === 'openrouter' && modelProblem === null
      ? (catalogue.data?.models.find((entry) => entry.id.toLowerCase() === trimmedModelId) ?? null)
      : null
  const dirty = draft !== null && server !== null && !same(draft, server)
  const atDefaults = draft !== null && defaults !== null && same(draft, defaults)

  const edit = (changes: Partial<Briefings>) => {
    setDraft((current) => (current ? { ...current, ...changes } : current))
    setSaved(false)
  }

  const switchProvider = (provider: BriefingProviderId) => {
    if (!draft || draft.model.provider === provider) return
    rememberedIds.current[draft.model.provider] = draft.model.id
    setIdTouched(false)
    edit({ model: modelFor(provider, rememberedIds.current[provider], defaults) })
  }

  const submit = () => {
    if (!draft || instructionsProblem || modelProblem) return
    setSaved(false)
    save.mutate({ briefings: normalise(draft) }, { onSuccess: () => setSaved(true) })
  }

  return (
    <Card
      title="Objekt-Briefings"
      footer="Gilt für neue Briefings. Bereits erstellte bleiben, wie sie sind — jedes Briefing merkt sich, mit welcher Anweisung es entstanden ist."
    >
      {query.isPending && <p className="text-muted text-sm">Wird geladen …</p>}

      {query.isError && (
        <p className="text-danger text-sm">
          Einstellungen konnten nicht geladen werden: {errorMessage(query.error)}
        </p>
      )}

      {draft && (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          <p className="text-muted text-sm leading-relaxed">
            Ein Briefing wertet die Protokolle der Eigentümerversammlungen und die Beschlusssammlung
            eines Objekts aus. Hier legen Sie fest, welches Modell das tut und worauf es achten
            soll.
          </p>

          <div className="flex flex-col gap-3">
            {/* A fieldset, not a Field: Field is a <label>, and a label around
                a row of buttons forwards a click on its caption to the first. */}
            <fieldset className="m-0 min-w-0 border-0 p-0">
              <legend className="text-muted mb-1 block p-0 text-[11px] tracking-wide uppercase">
                Anbieter
              </legend>
              <Segmented
                value={draft.model.provider}
                options={PROVIDER_OPTIONS}
                disabled={busy}
                onChange={switchProvider}
              />
            </fieldset>

            {draft.model.provider === 'anthropic' ? (
              <Field label="Modell">
                <select
                  className={CONTROL}
                  value={draft.model.id}
                  disabled={busy}
                  onChange={(event) =>
                    edit({
                      model: { provider: 'anthropic', id: event.target.value as AnthropicModelId },
                    })
                  }
                >
                  {ANTHROPIC_MODEL_IDS.map((id) => (
                    <option key={id} value={id}>
                      {MODEL_LABELS[id]}
                    </option>
                  ))}
                </select>
              </Field>
            ) : draft.model.provider === 'mistral' ? (
              <Field label="Modell">
                <select
                  className={CONTROL}
                  value={draft.model.id}
                  disabled={busy}
                  onChange={(event) =>
                    edit({
                      model: { provider: 'mistral', id: event.target.value as MistralModelId },
                    })
                  }
                >
                  {MISTRAL_MODEL_IDS.map((id) => (
                    <option key={id} value={id}>
                      {MODEL_LABELS[id]}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <div>
                <Field label="Modell (OpenRouter-ID)">
                  <input
                    type="text"
                    className={`${CONTROL} font-mono`}
                    value={draft.model.id}
                    disabled={busy}
                    list={datalistId}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoCorrect="off"
                    autoComplete="off"
                    placeholder={DEFAULT_OPENROUTER_MODEL}
                    aria-invalid={showModelProblem ? true : undefined}
                    aria-describedby={modelHintId}
                    onBlur={() => setIdTouched(true)}
                    onChange={(event) =>
                      edit({ model: { provider: 'openrouter', id: event.target.value } })
                    }
                  />
                </Field>
                <datalist id={datalistId}>
                  {catalogue.data
                    ? catalogue.data.models.map((entry) => (
                        <option key={entry.id} value={entry.id} label={entry.name} />
                      ))
                    : OPENROUTER_BRIEFING_SUGGESTIONS.map((entry) => (
                        <option key={entry.id} value={entry.id} label={entry.label} />
                      ))}
                </datalist>

                <div id={modelHintId} className="mt-1 space-y-0.5 text-[11px]">
                  {showModelProblem ? (
                    <p className="text-danger">{modelProblem}</p>
                  ) : modelProblem ? null : catalogueEntry ? (
                    <p className="text-faint">{capabilityHint(catalogueEntry)}</p>
                  ) : catalogue.isSuccess ? (
                    <p className="text-faint">
                      Nicht im OpenRouter-Katalog gefunden — wird beim ersten Kostenvoranschlag
                      geprüft.
                    </p>
                  ) : catalogue.isFetching ? (
                    <p className="text-faint">OpenRouter-Katalog wird geladen …</p>
                  ) : null}

                  {catalogue.isError && !catalogue.isFetching && (
                    <p className="text-faint">
                      Der OpenRouter-Katalog konnte nicht geladen werden; vorgeschlagen werden daher
                      nur einige bewährte Modelle.{' '}
                      <button
                        type="button"
                        onClick={() => void catalogue.refetch()}
                        className="text-muted hover:text-ink underline underline-offset-2"
                      >
                        Erneut versuchen
                      </button>
                    </p>
                  )}
                </div>
              </div>
            )}

            <p className="text-faint text-[11px] leading-relaxed">
              Anthropic (direkt) liest die PDFs selbst, Seitenbilder eingeschlossen. OpenRouter
              bietet viele Modelle — gedacht, um Ergebnisse zu vergleichen. Mistral AI (EU) arbeitet
              ausschließlich in europäischen Rechenzentren. Den Text der PDFs liest das Werkzeug
              selbst; nur gescannte Seiten ohne Textebene werden per Mistral-OCR erkannt.
            </p>
          </div>

          <div>
            <Field
              label="Anweisung"
              hint="Gliederung, Grundregeln und Gestaltung des Briefings legt das Werkzeug fest. Dieser Text bestimmt den Inhalt: worauf geachtet wird und wie formuliert wird."
            >
              {/* A plain textarea: the shared TextArea fixes its own classes,
                  and a prompt reads better set like code, tall, in mono. */}
              <textarea
                value={draft.instructions}
                disabled={busy}
                rows={18}
                spellCheck={false}
                aria-invalid={instructionsProblem ? true : undefined}
                aria-describedby={counterId}
                onChange={(event) => edit({ instructions: event.target.value })}
                className={`${CONTROL} resize-y font-mono text-xs leading-relaxed`}
              />
            </Field>
            <p
              id={counterId}
              className={`mt-1 text-right text-[11px] tabular-nums ${
                instructionsProblem ? 'text-danger' : 'text-faint'
              }`}
            >
              {instructionsLength.toLocaleString('de-DE')} /{' '}
              {INSTRUCTIONS_MAX.toLocaleString('de-DE')} Zeichen
              {instructionsProblem && ` — ${instructionsProblem}`}
            </p>
          </div>

          <Field
            label="Standard-Sonderfragen"
            hint="Werden vorgeschlagen, wenn beim Erstellen keine eigenen Sonderfragen eingetragen sind. Leer lassen für keine."
          >
            <TextArea
              value={draft.defaultSpecialQuestions}
              disabled={busy}
              rows={3}
              maxLength={QUESTIONS_MAX}
              placeholder="z. B. Leitungen, Aufzug, Dach"
              onChange={(event) => edit({ defaultSpecialQuestions: event.target.value })}
            />
          </Field>

          <div className="border-line flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-4">
            <button
              type="submit"
              disabled={!dirty || busy || instructionsProblem !== null || modelProblem !== null}
              className="bg-brand text-surface hover:bg-brand-strong px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
            >
              {busy ? 'wird gespeichert …' : 'Speichern'}
            </button>

            {dirty && (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  if (!server) return
                  rememberedIds.current = { [server.model.provider]: server.model.id }
                  setIdTouched(false)
                  setDraft({ ...server })
                }}
                className="text-muted hover:text-ink text-xs underline underline-offset-4 transition-colors disabled:opacity-40"
              >
                Verwerfen
              </button>
            )}

            {saved && !dirty && <span className="text-positive text-xs">Gespeichert.</span>}

            {/* Loads the defaults INTO the draft — a starting point to look at,
                not an action that reaches the server on its own. */}
            <button
              type="button"
              disabled={busy || atDefaults || !defaults}
              onClick={() => {
                if (!defaults) return
                // Keep what is in the draft now, so switching back to its
                // provider after restoring gives it back.
                if (draft) rememberedIds.current[draft.model.provider] = draft.model.id
                edit({ ...defaults })
              }}
              className="text-muted hover:text-ink ml-auto text-xs underline underline-offset-4 transition-colors disabled:opacity-40"
            >
              Standard wiederherstellen
            </button>
          </div>

          {save.isError && <p className="text-danger text-xs">{errorMessage(save.error)}</p>}
        </form>
      )}
    </Card>
  )
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message
  if (error instanceof Error) return error.message
  return 'Unbekannter Fehler'
}
